import type {
  ApiProblemShape,
  AnalyticsSummary,
  Campaign,
  CampaignAction,
  CampaignReport,
  CreateCampaignInput,
  CreativeUpload,
  MarketPolicyInput,
  MarketPolicyResult,
  ModerationReport,
  PortalMe,
  PortalSession,
  ReviewCampaign,
} from './types';

const TOKEN_KEY = 'topictalk.portal.access-token.v1';
const API_ORIGIN = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/u, '');
const PORTAL_ROOT = `${API_ORIGIN}/v1/portal`;

export class PortalApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;

  constructor(message: string, status: number, code: string, requestId?: string) {
    super(message);
    this.name = 'PortalApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

function persistedToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

async function toApiError(response: Response): Promise<PortalApiError> {
  let problem: ApiProblemShape = {};
  try {
    problem = (await response.json()) as ApiProblemShape;
  } catch {
    // A proxy or network edge can return a non-JSON error body.
  }
  return new PortalApiError(
    problem.detail ?? problem.title ?? `Die Anfrage ist fehlgeschlagen (${response.status}).`,
    response.status,
    problem.code ?? 'REQUEST_FAILED',
    problem.requestId,
  );
}

interface RequestOptions extends RequestInit {
  authenticated?: boolean;
}

export class PortalApi {
  private token: string | null = persistedToken();

  hasPersistedToken(): boolean {
    return Boolean(this.token);
  }

  setToken(token: string): void {
    this.token = token;
    try {
      window.localStorage.setItem(TOKEN_KEY, token);
    } catch {
      // The current session remains usable if durable browser storage is disabled.
    }
  }

  clearToken(): void {
    this.token = null;
    try {
      window.localStorage.removeItem(TOKEN_KEY);
    } catch {
      // Nothing else to revoke client-side.
    }
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const headers = new Headers(options.headers);
    const authenticated = options.authenticated ?? true;
    if (authenticated) {
      if (!this.token) throw new PortalApiError('Bitte erneut anmelden.', 401, 'AUTH_REQUIRED');
      headers.set('Authorization', `Bearer ${this.token}`);
    }
    if (options.body && !(options.body instanceof Blob) && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }
    let response: Response;
    try {
      response = await fetch(`${PORTAL_ROOT}${path}`, { ...options, headers });
    } catch {
      throw new PortalApiError(
        'Die TopicTalk-API ist derzeit nicht erreichbar. Prüfe die Verbindung und versuche es erneut.',
        0,
        'NETWORK_ERROR',
      );
    }
    if (!response.ok) throw await toApiError(response);
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  async login(email: string, password: string): Promise<PortalSession> {
    const session = await this.request<PortalSession>('/login', {
      method: 'POST',
      authenticated: false,
      body: JSON.stringify({ email, password }),
    });
    this.setToken(session.accessToken);
    return session;
  }

  async bootstrap(input: {
    email: string;
    password: string;
    legalName: string;
    displayName: string;
    countryCode: string;
    bootstrapKey: string;
  }): Promise<PortalSession> {
    const session = await this.request<PortalSession>('/bootstrap', {
      method: 'POST',
      authenticated: false,
      headers: { 'X-Bootstrap-Key': input.bootstrapKey },
      body: JSON.stringify({
        email: input.email,
        password: input.password,
        legalName: input.legalName,
        displayName: input.displayName,
        countryCode: input.countryCode,
      }),
    });
    this.setToken(session.accessToken);
    return session;
  }

  me(): Promise<PortalMe> {
    return this.request<PortalMe>('/me');
  }

  async logout(): Promise<void> {
    const revocation = this.request<void>('/logout', { method: 'POST' });
    this.clearToken();
    await revocation;
  }

  async campaigns(): Promise<Campaign[]> {
    const result = await this.request<{ items: Campaign[] }>('/campaigns');
    return result.items;
  }

  createCampaign(input: CreateCampaignInput): Promise<{ id: string; state: 'draft' }> {
    return this.request('/campaigns', { method: 'POST', body: JSON.stringify(input) });
  }

  transitionCampaign(id: string, action: CampaignAction): Promise<{ id: string; state: Campaign['state'] }> {
    return this.request(`/campaigns/${encodeURIComponent(id)}/state`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
  }

  campaignReport(id: string): Promise<CampaignReport> {
    return this.request(`/campaigns/${encodeURIComponent(id)}/report`);
  }

  analyticsSummary(): Promise<AnalyticsSummary> {
    return this.request('/analytics/summary');
  }

  async reviewCampaigns(): Promise<ReviewCampaign[]> {
    const result = await this.request<{ items: ReviewCampaign[] }>('/admin/campaigns?state=submitted');
    return result.items;
  }

  uploadCreative(file: File): Promise<CreativeUpload> {
    return this.request(`/assets/${encodeURIComponent(file.name)}`, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
  }

  updateMarketPolicy(countryCode: string, input: MarketPolicyInput): Promise<MarketPolicyResult> {
    return this.request(`/admin/market-policies/${encodeURIComponent(countryCode.toUpperCase())}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  async marketPolicies(): Promise<MarketPolicyResult[]> {
    const result = await this.request<{ items: MarketPolicyResult[] }>('/admin/market-policies');
    return result.items;
  }

  async moderationReports(): Promise<ModerationReport[]> {
    const result = await this.request<{ items: ModerationReport[] }>('/admin/reports');
    return result.items;
  }

  claimModerationReport(id: string): Promise<ModerationReport> {
    return this.request(`/admin/reports/${encodeURIComponent(id)}/claim`, { method: 'POST' });
  }

  closeModerationReport(
    id: string,
    action: 'resolve' | 'dismiss',
    resolutionCode: string,
  ): Promise<ModerationReport> {
    return this.request(`/admin/reports/${encodeURIComponent(id)}/${action}`, {
      method: 'POST',
      body: JSON.stringify({ resolutionCode }),
    });
  }
}

export const portalApi = new PortalApi();
